import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Bell } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useModule } from "@/context/ModuleContext";
import { api } from "@/lib/api";

export default function ApprovalBell() {
  const [counts, setCounts] = useState({ pr: 0, po: 0, total: 0, can_approve: false });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { hasModule } = useModule();
  const canSeeApprovals = hasModule("purchase");

  const load = useCallback(async () => {
    if (!canSeeApprovals) return;
    try {
      const { data } = await api.get("/purchase/approval-counts");
      setCounts(data);
    } catch {
      // silent — bell simply stays hidden
    }
  }, [canSeeApprovals]);

  useEffect(() => { load(); }, [load, location.pathname]);
  useEffect(() => {
    if (!canSeeApprovals) return;
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load, canSeeApprovals]);

  if (!canSeeApprovals || !counts.can_approve) return null;

  const go = (to) => { setOpen(false); navigate(to); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="approval-bell"
          className="relative h-8 w-8 flex items-center justify-center rounded-md text-foreground hover:bg-muted transition-colors"
          title="Approvals awaiting you"
        >
          <Bell size={18} weight="bold" />
          {counts.total > 0 && (
            <span
              data-testid="approval-bell-count"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold leading-none"
            >
              {counts.total > 99 ? "99+" : counts.total}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0 overflow-hidden" data-testid="approval-bell-panel">
        <div className="px-4 py-3 border-b bg-muted/40">
          <div className="text-sm font-semibold">Awaiting your approval</div>
          <div className="text-xs text-muted-foreground">{counts.total} item{counts.total === 1 ? "" : "s"} pending</div>
        </div>
        <button
          onClick={() => go("/purchase/requisitions?pending=1")}
          data-testid="approval-bell-pr"
          className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-primary/5 transition-colors border-b"
        >
          <span>Purchase Requisitions</span>
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${counts.pr > 0 ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-slate-100 text-slate-500 border border-slate-200"}`}>{counts.pr}</span>
        </button>
        <button
          onClick={() => go("/purchase/orders?status=Pending Approval")}
          data-testid="approval-bell-po"
          className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-primary/5 transition-colors"
        >
          <span>Purchase Orders</span>
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${counts.po > 0 ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-slate-100 text-slate-500 border border-slate-200"}`}>{counts.po}</span>
        </button>
        {counts.total === 0 && (
          <div className="px-4 py-3 text-xs text-muted-foreground text-center">You're all caught up 🎉</div>
        )}
      </PopoverContent>
    </Popover>
  );
}
